import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Solution } from '../types';

interface ComparisonPanelProps {
  solutions: Solution[];
  selectedIds: string[];
  onRemove: (id: string) => void;
  onClear: () => void;
}

const ComparisonPanel = ({ solutions, selectedIds, onRemove, onClear }: ComparisonPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedSolutions = solutions.filter(s => selectedIds.includes(s.id));

  if (selectedIds.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-brand-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Icon name="GitCompare" size={24} className="text-primary" />
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Compare Solutions ({selectedIds.length})
                </h3>
                <p className="text-sm text-text-secondary">
                  Select up to 4 solutions to compare
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-muted rounded-md transition-colors duration-300"
                aria-label="Toggle comparison view"
              >
                <Icon name={isExpanded ? 'ChevronDown' : 'ChevronUp'} size={20} />
              </button>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                iconPosition="left"
                onClick={onClear}
              >
                Clear All
              </Button>
            </div>
          </div>

          {isExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {selectedSolutions.map((solution) => (
                <div key={solution.id} className="bg-muted rounded-lg p-4 relative">
                  <button
                    onClick={() => onRemove(solution.id)}
                    className="absolute top-2 right-2 p-1 bg-background rounded-full hover:bg-destructive hover:text-destructive-foreground transition-colors duration-300"
                    aria-label="Remove from comparison"
                  >
                    <Icon name="X" size={16} />
                  </button>
                  <h4 className="font-semibold text-foreground mb-2 pr-6">{solution.title}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="Target" size={14} className="text-success" />
                      <span className="text-text-secondary">{solution.accuracy}% Accuracy</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={14} className="text-primary" />
                      <span className="text-text-secondary">{solution.deploymentTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Layers" size={14} className="text-accent" />
                      <span className="text-text-secondary">{solution.features.length} Features</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="GitCompare"
              iconPosition="left"
              disabled={selectedIds.length < 2}
              onClick={() => console.log('View detailed comparison')}
            >
              View Detailed Comparison
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;